/**
 * FinancialTab — the customer's financial cockpit, rendered in the Customer
 * detail ExperienceView when the "Financial" tab is active (replacing the plain
 * field view). Two columns, matching the Loyalty tab / details-page system:
 *
 *   • Left (main): a document switcher (Sales Orders / Quotations / Sales
 *     Invoices / Payment Entries) over a server-paginated table with numbered
 *     pages (1 2 3 …). Each row opens the underlying document.
 *   • Right (sidebar): a financial summary (total spent, outstanding, credit
 *     limit, avg order) + the read-only selling defaults.
 *
 * All data filters by the customer and is paged server-side, so it scales to
 * any volume without loading everything up front.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon, FileText, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { getERPClient } from "@/lib/erp/client";
import { routes } from "@/lib/config/routes";
import { useCustomerOrders } from "@/features/customers/api/customerHooks";
import { formatMoney, formatReadableDate } from "./customerDetailPrimitives";
import { MetaFieldEditModal } from "@/features/inventory/item-detail/components/MetaFieldEditModal";
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { SalesOrder } from "@/lib/erp/types/order";

/** Fields shown in the combined "Selling defaults + Credit limit" settings box. */
const SETTINGS_FIELDNAMES = ["default_currency", "default_price_list", "payment_terms", "credit_limits"] as const;

const PAGE_SIZE = 10;

interface DocConfig {
  id: string;
  label: string;
  doctype: string;
  filters: (customer: string) => Record<string, any>;
  fields: string[];
  dateField: string;
  amountField: string;
  route: (name: string) => string;
}

const DOC_CONFIGS: DocConfig[] = [
  {
    id: "orders",
    label: "คำสั่งซื้อ",
    doctype: "Sales Order",
    filters: (c) => ({ customer: c }),
    fields: ["name", "transaction_date", "status", "grand_total", "currency"],
    dateField: "transaction_date",
    amountField: "grand_total",
    route: (n) => routes.salesOrder.detail(n),
  },
  {
    id: "quotes",
    label: "ใบเสนอราคา",
    doctype: "Quotation",
    filters: (c) => ({ party_name: c, order_type: ["!=", "Shopping Cart"] }),
    fields: ["name", "transaction_date", "status", "grand_total", "currency"],
    dateField: "transaction_date",
    amountField: "grand_total",
    route: (n) => `/doc/Quotation/${encodeURIComponent(n)}`,
  },
  {
    id: "invoices",
    label: "ใบแจ้งหนี้",
    doctype: "Sales Invoice",
    filters: (c) => ({ customer: c }),
    fields: ["name", "posting_date", "status", "grand_total", "currency"],
    dateField: "posting_date",
    amountField: "grand_total",
    route: (n) => `/doc/Sales Invoice/${encodeURIComponent(n)}`,
  },
  {
    id: "payments",
    label: "การชำระเงิน",
    doctype: "Payment Entry",
    filters: (c) => ({ party_type: "Customer", party: c }),
    fields: ["name", "posting_date", "status", "paid_amount", "paid_to_account_currency"],
    dateField: "posting_date",
    amountField: "paid_amount",
    route: (n) => `/doc/Payment Entry/${encodeURIComponent(n)}`,
  },
];

/** 1-based page numbers with ellipsis, e.g. 1 … 4 5 6 … 20. */
function pageList(page: number, totalPages: number): Array<number | "…"> {
  const cur = page + 1;
  const wanted = new Set([1, totalPages, cur, cur - 1, cur + 1]);
  const sorted = [...wanted].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out: Array<number | "…"> = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

function useFinancialCounts(customer: string) {
  return useQuery({
    queryKey: ["customers", "financial-counts", customer],
    enabled: !!customer,
    staleTime: 60_000,
    queryFn: async () => {
      const client = getERPClient();
      const entries = await Promise.all(
        DOC_CONFIGS.map((c) =>
          client.getCount(c.doctype, c.filters(customer)).catch(() => 0),
        ),
      );
      return Object.fromEntries(DOC_CONFIGS.map((c, i) => [c.id, entries[i]])) as Record<string, number>;
    },
  });
}

function useFinancialDocs(cfg: DocConfig, customer: string, page: number) {
  return useQuery({
    queryKey: ["customers", "financial-docs", cfg.id, customer, page],
    enabled: !!customer,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: () =>
      getERPClient().listWithPagination<Record<string, any>>({
        doctype: cfg.doctype,
        filters: cfg.filters(customer),
        fields: cfg.fields,
        limit_start: page * PAGE_SIZE,
        limit_page_length: PAGE_SIZE,
        order_by: `${cfg.dateField} desc`,
      }),
  });
}

function StatusPill({ status }: { status?: string }) {
  if (!status) return null;
  const tone = /paid|completed|fully|submitted/i.test(status)
    ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300"
    : /cancel|return|overdue|unpaid/i.test(status)
      ? "bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300"
      : "bg-muted text-muted-foreground";
  return (
    <span className={cn("inline-block rounded-full px-2 py-0.5 text-[11px] font-medium", tone)}>{status}</span>
  );
}

export function FinancialTab({
  customer,
  formData,
  metaFields,
  onFieldChange,
  onSaveSuccess,
  documentName,
}: {
  customer: string;
  formData: Record<string, any>;
  /** Full Customer doctype field schemas — used to drive the settings edit modal. */
  metaFields?: DocFieldSchema[];
  onFieldChange?: (fieldname: string, value: any) => void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  documentName?: string;
}) {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("orders");
  const [page, setPage] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const cfg = DOC_CONFIGS.find((c) => c.id === activeId) ?? DOC_CONFIGS[0];

  // Field schemas for the combined settings modal (currency, price list,
  // payment terms, credit-limit child table), in display order.
  const settingsFields = (metaFields ?? [])
    .filter((f): f is DocFieldSchema => SETTINGS_FIELDNAMES.includes(f.fieldname as (typeof SETTINGS_FIELDNAMES)[number]))
    .sort(
      (a, b) =>
        SETTINGS_FIELDNAMES.indexOf(a.fieldname as (typeof SETTINGS_FIELDNAMES)[number]) -
        SETTINGS_FIELDNAMES.indexOf(b.fieldname as (typeof SETTINGS_FIELDNAMES)[number]),
    );
  const canEditSettings = settingsFields.length > 0 && !!onFieldChange && !!documentName;

  const counts = useFinancialCounts(customer).data;
  const { data, isLoading, isFetching } = useFinancialDocs(cfg, customer, page);
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const switchDoc = (id: string) => {
    setActiveId(id);
    setPage(0);
  };

  // Sidebar summary — reuse the orders the AccountFinancialCard loads.
  const { data: orders } = useCustomerOrders(customer);
  const currency = (formData.default_currency as string) || "THB";
  const active = ((orders as SalesOrder[]) ?? []).filter((o) => (o as Record<string, any>).status !== "Cancelled");
  const totalSpent = active.reduce((s, o) => s + (Number((o as Record<string, any>).grand_total) || 0), 0);
  const outstanding = active.reduce((s, o) => {
    const rec = o as Record<string, any>;
    const billed = Math.min(Number(rec.per_billed) || 0, 100);
    return s + (Number(rec.grand_total) || 0) * (1 - billed / 100);
  }, 0);
  const avgOrder = active.length > 0 ? totalSpent / active.length : 0;
  const creditRows = Array.isArray(formData.credit_limits) ? formData.credit_limits : [];
  const creditLimit =
    creditRows.length > 0 && creditRows[0]?.credit_limit != null ? Number(creditRows[0].credit_limit) : null;

  return (
    <div className="flex flex-col gap-6 xl:flex-row">
      {/* Main column — document switcher + paginated table. */}
      <div className="min-w-0 flex-1">
        <div className={CARD}>
          <div className="p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                เอกสารการเงิน
              </span>
            </div>

            {/* Doctype switcher */}
            <div className="mb-4 inline-flex flex-wrap gap-0.5 rounded-lg bg-muted/60 p-0.5">
              {DOC_CONFIGS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => switchDoc(c.id)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    activeId === c.id
                      ? "bg-white text-foreground shadow-sm dark:bg-gray-700"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {c.label}
                  {counts?.[c.id] != null && <span className="ml-1 opacity-60">{counts[c.id]}</span>}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead>
                  <tr className="border-b border-border/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-2 pr-3 font-semibold">วันที่</th>
                    <th className="py-2 pr-3 font-semibold">เลขที่</th>
                    <th className="py-2 pr-3 font-semibold">สถานะ</th>
                    <th className="py-2 pl-3 text-right font-semibold">ยอดเงิน</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-muted-foreground">
                        กำลังโหลด…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-10 text-center text-muted-foreground">
                        ไม่มีเอกสาร
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr
                        key={r.name}
                        onClick={() => navigate(cfg.route(r.name))}
                        className="cursor-pointer border-b border-border/30 transition-colors last:border-0 hover:bg-muted/40"
                      >
                        <td className="py-2.5 pr-3 tabular-nums text-muted-foreground">
                          {formatReadableDate(r[cfg.dateField])}
                        </td>
                        <td className="py-2.5 pr-3 font-medium text-foreground">{r.name}</td>
                        <td className="py-2.5 pr-3">
                          <StatusPill status={r.status} />
                        </td>
                        <td className="py-2.5 pl-3 text-right font-semibold tabular-nums text-foreground">
                          {formatMoney(Number(r[cfg.amountField]) || 0, r.currency || r.paid_to_account_currency || currency)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Numbered pagination */}
            {total > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between border-t border-border/40 pt-3">
                <span className="text-[11px] text-muted-foreground">
                  {total.toLocaleString()} รายการ{isFetching ? " · …" : ""}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    aria-label="ก่อนหน้า"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {pageList(page, totalPages).map((n, i) =>
                    n === "…" ? (
                      <span key={`e${i}`} className="px-1 text-[12px] text-muted-foreground">
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPage(n - 1)}
                        className={cn(
                          "h-7 min-w-7 rounded-lg border px-2 text-[12px] font-medium transition-colors",
                          n - 1 === page
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border/60 text-muted-foreground hover:bg-muted/50",
                        )}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    aria-label="ถัดไป"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar — financial summary + selling defaults. */}
      <div className="order-first w-full shrink-0 space-y-4 xl:order-last xl:w-[360px]">
        <div className={CARD}>
          <div className="p-5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              ยอดซื้อรวม
            </div>
            <div className="mt-0.5 text-[28px] font-bold leading-tight tracking-tight text-foreground">
              {formatMoney(totalSpent, currency)}
            </div>
            <div className="mt-4 space-y-2.5 border-t border-border/40 pt-4 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">ค้างชำระ</span>
                <span className={cn("font-medium", outstanding > 0.5 ? "text-amber-600 dark:text-amber-400" : "text-foreground")}>
                  {formatMoney(outstanding > 0.5 ? outstanding : 0, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">ยอดเฉลี่ย/ออเดอร์</span>
                <span className="font-medium text-foreground">{formatMoney(avgOrder, currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Combined selling-defaults + credit-limit settings — click to edit in modal. */}
        <button
          type="button"
          onClick={() => canEditSettings && setSettingsOpen(true)}
          disabled={!canEditSettings}
          className={cn(
            CARD,
            "w-full text-left transition-colors",
            canEditSettings ? "cursor-pointer hover:bg-muted/30" : "cursor-default",
          )}
        >
          <div className="p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  ค่าตั้งต้นการขาย
                </span>
              </div>
              {canEditSettings && <ChevronRightIcon className="h-4 w-4 text-muted-foreground/60" />}
            </div>
            <div className="space-y-2.5 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">สกุลเงิน</span>
                <span className="font-medium text-foreground">{(formData.default_currency as string) || "—"}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">รายการราคา</span>
                <span className="max-w-[60%] truncate font-medium text-foreground" title={(formData.default_price_list as string) || undefined}>
                  {(formData.default_price_list as string) || "—"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">เงื่อนไขชำระเงิน</span>
                <span className="max-w-[60%] truncate font-medium text-foreground" title={(formData.payment_terms as string) || undefined}>
                  {(formData.payment_terms as string) || "—"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">วงเงินเครดิต</span>
                <span className="font-medium text-foreground">
                  {creditLimit != null ? formatMoney(creditLimit, currency) : "ไม่จำกัด"}
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>

      {canEditSettings && (
        <MetaFieldEditModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          fields={settingsFields}
          title="ค่าตั้งต้นการขายและเครดิต"
          contentClassName="md:max-w-[80vw]"
          formData={formData}
          onFieldChange={onFieldChange!}
          onSaveSuccess={onSaveSuccess}
          documentName={documentName}
        />
      )}
    </div>
  );
}
